import moment from "moment";
import { ToastService } from "../../../../utils/toastify";

const HosValidators = {
  /**
   *
   * @param {object} record current hos record
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordEndDateInPast: (record, warn = false) => {
    // endDate and endTime must be greater than the startDate and startTime of this record
    const { startDate, endDate } = record;
    if (moment(startDate).isAfter(moment(endDate))) {
      warn && ToastService.Error("Invalid Entry: ", "End Date/Time must not be in the past!");
      return false;
    }

    return true;
  },
  /**
   * @param {object} record current hos record
   * @param {object | null} lastRecord last hos record (for comparison)
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordForDuplicateInterruptedRest: (record, lastRecord, warn = false) => {
    // check for consecutive Interrupted Rest records with same date/time
    const { startDate, timezone } = record;
    if (
      lastRecord &&
      lastRecord.dutyType === "Interrupted Rest" &&
      moment.tz(startDate, timezone).isSame(moment.tz(lastRecord.startDate, timezone))
    ) {
      warn && ToastService.Error("Invalid Entry: ", "Cannot add duplicate Interrupted Rest record!");
      return false;
    }

    return true;
  },
  /**
   * @param {object} record current hos record
   * @param {object | null} lastRecord last hos record (for comparison)
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordForInterruptedRestWithoutGap: (record, lastRecord, warn = false) => {
    // check for interrupted rest added at the end of last record
    const { startDate, timezone } = record;
    if (
      lastRecord &&
      lastRecord.dutyType !== "Interrupted Rest" &&
      moment.tz(startDate, timezone).isSame(moment.tz(lastRecord.endDate, timezone))
    ) {
      warn && ToastService.Error("Invalid Entry: ", "Cannot add Interrupted Rest without starting the rest period!");
      return false;
    }

    return true;
  },
  /**
   *
   * @param {object} record current hos record
   * @param {object | null} lastRecord last hos record (for comparison)
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordForConflicts: (record, lastRecord, warn = false) => {
    const { startDate, endDate, endLocCode, serviceType } = record;
    if (record.dutyType !== "Interrupted Rest" && moment(startDate).isSame(moment(endDate))) {
      warn && ToastService.Error("Invalid Entry: ", "End Date/Time must not be the same as Start Date/Time!");
      return false;
    }
    // endDate and endTime must be greater than the startDate and startTime of this record
    if (moment(startDate).isAfter(moment(endDate))) {
      warn && ToastService.Error("Invalid Entry: ", "End Date/Time must not be in the past!");
      return false;
    }
    if (lastRecord) {
      const { endDate } = lastRecord;
      if (lastRecord.dutyType === "Interrupted Rest") {
        // startDate of current record must be greater than endDate of the last record
        if (moment(startDate).isSameOrBefore(moment(endDate))) {
          warn && ToastService.Error("Invalid Entry: ", "Cannot add an entry at the time of Interrupted Rest!");
          return false;
        }
      } else {
        // startDate of current record must be greater than or equal to endDate of the last record
        if (moment(startDate).isBefore(moment(endDate))) {
          warn && ToastService.Error("Invalid Entry: ", "Cannot add an entry before the last activity!");
          return false;
        }
      }
    }

    return true;
  },
  /**
   *
   * @param {object} record current hos record
   * @param {object | null} lastRecord last hos record (for comparison)
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordForLocationConflict: (record, lastRecord, warn = false) => {
    const { startDate, endDate, startLocCode, serviceType } = record;
    if (lastRecord) {
      const { endDate } = lastRecord;
      if (moment(startDate).isSame(moment(endDate)) && lastRecord.endLocCode && startLocCode !== lastRecord.endLocCode) {
        const loc = lastRecord.endLoc && lastRecord.endLoc.description;
        warn &&
          ToastService.Error(
            "Invalid Entry: ",
            `Start location of current '${serviceType}' must match the end location '${loc}' of previous '${lastRecord.serviceType}'!`,
          );
        return false;
      }
    }

    return true;
  },
  /**
   *
   * @param {object} record current hos record
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateHosRecordForMoreThan24Hours: (record, warn = false) => {
    // the entered data must not be for more than 24 hours
    const { startDate, endDate } = record;
    if (startDate && endDate && moment(endDate).diff(moment(startDate), "hours") >= 24) {
      warn && ToastService.Error("Invalid Entry:", "Cannot add an HOS entry equal to or exceeding 24 hours.");
      return false;
    }
    return true;
  },
  /**
   *
   * @param {object} record current hos record
   * @param {boolean} warn warning flag to indicate whether to warn the user through ToastService
   * @returns true if validated successfully, false otherwise.
   */
  validateMinCriteriaForHosRecord: (record, warn = false) => {
    if (record.dutyType && record.startDate && record.endDate) {
      record.startDate = moment(record.startDate).set("second", 0);
      record.startDate = moment(record.startDate).set("millisecond", 0);
      record.endDate = moment(record.endDate).set("second", 0);
      record.endDate = moment(record.endDate).set("millisecond", 0);
      return true;
    }
    warn &&
      ToastService.Error(
        "Error: ",
        "End Date & Time missing for last the HOS record. Before adding a new one, please fill in the end time for last activity.",
      );
    return false;
  },
  validateMealDuration(record, warn = false) {
    const isAtLeast30MinutesApart = (start, end) => {
      const diffMinutes = moment(end).diff(moment(start), "minutes");
      return diffMinutes >= 30;
    };
    const { startDate, endDate } = record;
    let valid = isAtLeast30MinutesApart(startDate, endDate);
    !valid && warn && ToastService.Error("Error: ", "Meals 29 minutes or less must be added as Normal Covered Service.");
    return valid;
  },
};
export default HosValidators;



// WEBPACK FOOTER //
// ./src/components/HoursOfService/Helper/HosValidators/index.js